import React, { useMemo, useState } from 'react'
import {
  coerceInstructionDocument,
  partitionInstructionSections,
  toSentenceCase,
  type InstructionDocument,
  type InstructionItem,
  type InstructionSection,
} from '../utils/instructionSections'

type Props = {
  text: string | null | undefined
  document?: InstructionDocument | string | null
  title?: string
  className?: string
}

function ThresholdBadge() {
  return (
    <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-50 tw-px-1.5 tw-py-0 tw-text-[10px] tw-font-medium tw-text-amber-800 tw-ring-1 tw-ring-amber-100">
      Threshold
    </span>
  )
}

function ItemList({
  items,
  style,
}: {
  items: InstructionItem[]
  style?: 'numbered' | 'lettered' | 'bullet'
}) {
  const Tag = style === 'bullet' || !style ? 'ul' : 'ol'
  const listClass =
    style === 'numbered' ? 'tw-list-decimal' : style === 'lettered' ? '' : 'tw-list-disc'
  return (
    <Tag
      className={`tw-m-0 tw-space-y-1 tw-pl-5 ${listClass}`}
      style={style === 'lettered' ? { listStyleType: 'upper-alpha' } : undefined}
    >
      {items.map((item, i) => (
        <li key={i} className="tw-text-sm tw-leading-relaxed tw-text-slate-700">
          <span className="tw-inline-flex tw-flex-wrap tw-items-baseline tw-gap-1.5">
            {item.text}
            {item.severity === 'flag' ? <ThresholdBadge /> : null}
          </span>
        </li>
      ))}
    </Tag>
  )
}

function SectionCard({ section }: { section: InstructionSection }) {
  const heading = section.title ? toSentenceCase(section.title) : null

  if (section.type === 'paragraph') {
    return (
      <section className="tw-space-y-1">
        {heading ? (
          <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-slate-800">{heading}</h3>
        ) : null}
        <p className="tw-m-0 tw-text-sm tw-leading-relaxed tw-text-slate-700">{section.text}</p>
      </section>
    )
  }

  if (section.type === 'context') {
    const dates = [section.startDate, section.endDate].filter(Boolean).join(' – ')
    return (
      <section className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-2.5 tw-space-y-1.5">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          {heading ? (
            <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-slate-800">{heading}</h3>
          ) : (
            <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-slate-800">Context</h3>
          )}
          {dates ? (
            <span className="tw-inline-flex tw-rounded-full tw-bg-sky-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-sky-900 tw-ring-1 tw-ring-sky-100">
              {dates}
            </span>
          ) : null}
        </div>
        {section.text ? (
          <p className="tw-m-0 tw-text-sm tw-leading-relaxed tw-text-slate-700">{section.text}</p>
        ) : null}
        {section.items?.length ? <ItemList items={section.items} style="lettered" /> : null}
      </section>
    )
  }

  if (section.type === 'guardrails') {
    return (
      <section className="tw-rounded-lg tw-border tw-border-amber-100 tw-bg-amber-50/50 tw-p-2.5 tw-space-y-1.5">
        {heading ? (
          <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-slate-800">{heading}</h3>
        ) : (
          <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-slate-800">Watch for</h3>
        )}
        <ItemList items={section.items} style="bullet" />
      </section>
    )
  }

  if (section.type === 'output_format') {
    return (
      <section className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-2.5 tw-space-y-1.5">
        {heading ? (
          <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-slate-800">{heading}</h3>
        ) : (
          <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-slate-800">Output</h3>
        )}
        <ItemList items={section.items} style="numbered" />
      </section>
    )
  }

  if (section.type === 'checklist') {
    return (
      <section className="tw-space-y-1.5">
        {heading ? (
          <h3 className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-slate-800">{heading}</h3>
        ) : null}
        <ItemList items={section.items} style={section.style} />
      </section>
    )
  }

  return null
}

function TechnicalDetails({ sections }: { sections: InstructionSection[] }) {
  const [open, setOpen] = useState(false)
  const items = sections.flatMap((section) =>
    section.type === 'technical' ? section.items : [],
  )
  if (!items.length) return null

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-text-slate-700 tw-cursor-pointer"
      >
        {open ? 'Hide technical details' : 'Technical details'}
      </button>
      {open ? (
        <pre className="tw-m-0 tw-mt-2 tw-max-h-48 tw-overflow-auto tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-2 tw-font-mono tw-text-[11px] tw-leading-relaxed tw-text-slate-700 tw-whitespace-pre-wrap tw-break-all">
          {items.map((item) => item.text).join('\n')}
        </pre>
      ) : null}
    </div>
  )
}

/** Structured instructions card. Renders by section.type; never by title text. */
export default function TaskInstructionsCard({
  text,
  document,
  title = 'Instructions',
  className = '',
}: Props) {
  const parsed = useMemo(() => coerceInstructionDocument(document), [document])
  const fallback = String(text || '').trim()
  const { visible, technical } = useMemo(
    () => partitionInstructionSections(parsed?.sections || []),
    [parsed],
  )

  if (!parsed && !fallback) return null

  return (
    <div
      className={`tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50/80 tw-p-3 sm:tw-p-4 ${className}`}
    >
      <div className="tw-mb-2 tw-text-xs tw-font-medium tw-text-slate-500">{title}</div>
      <div className="tw-space-y-2.5">
        {parsed
          ? visible.map((section, i) => (
              <SectionCard key={`${section.type}-${i}`} section={section} />
            ))
          : (
              <p className="tw-m-0 tw-text-sm tw-leading-relaxed tw-text-slate-700">{fallback}</p>
            )}
      </div>
      {parsed ? <div className="tw-mt-2.5"><TechnicalDetails sections={technical} /></div> : null}
    </div>
  )
}
